import { useState, useMemo, useCallback, forwardRef } from "react";
import Map, { Marker, Popup, NavigationControl } from "react-map-gl/maplibre";
import useSupercluster from "use-supercluster";
import type { PointFeature } from "supercluster";
import {
  Box,
  Text,
  Paper,
  Stack,
  Group,
  Anchor,
  Center,
  Popover,
  Image,
  Button,
} from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { useDisclosure } from "@mantine/hooks";
import { IconHome, IconBuilding, IconExternalLink } from "@tabler/icons-react";
import { StatusBadge } from "@/components/common";
import type { MapApartment } from "@/api/types";
import "maplibre-gl/dist/maplibre-gl.css";

// GeoJSON feature properties for apartments
interface ApartmentProperties extends MapApartment {
  cluster: false;
}

// GeoJSON feature properties for clusters
interface ClusterProperties {
  cluster: true;
  cluster_id: number;
  point_count: number;
  point_count_abbreviated: number;
}

type ApartmentFeature = PointFeature<ApartmentProperties>;
type ClusterFeature = PointFeature<ClusterProperties>;
type MapFeature = ApartmentFeature | ClusterFeature;

const mapContainerStyle = {
  minHeight: 400,
  flex: "1 0 400px",
  borderRadius: "0.5rem",
  overflow: "hidden",
};

// Convert apartments to GeoJSON features for supercluster
const apartmentsToGeoJSON = (apartments: MapApartment[]): ApartmentFeature[] =>
  apartments.map((apt) => ({
    type: "Feature",
    properties: { cluster: false as const, ...apt },
    geometry: { type: "Point", coordinates: [apt.longitude, apt.latitude] },
  }));

interface ApartmentMapViewProps {
  apartments: MapApartment[];
  isLoading?: boolean;
  officeCoords?: { latitude: number; longitude: number } | null;
}

export function ApartmentMapView({ apartments, isLoading, officeCoords }: ApartmentMapViewProps) {
  const [viewState, setViewState] = useState({
    longitude: 18.0686,
    latitude: 59.3293,
    zoom: 11,
  });
  const [selectedApartment, setSelectedApartment] = useState<MapApartment | null>(null);

  // Convert to GeoJSON
  const points = useMemo(() => apartmentsToGeoJSON(apartments), [apartments]);

  // Compute bounds for the map (include office if available)
  const bounds = useMemo((): [number, number, number, number] | undefined => {
    const allPoints = apartments.map((a) => ({ lng: a.longitude, lat: a.latitude }));
    if (officeCoords) {
      allPoints.push({ lng: officeCoords.longitude, lat: officeCoords.latitude });
    }
    if (allPoints.length === 0) return undefined;
    const lngs = allPoints.map((p) => p.lng);
    const lats = allPoints.map((p) => p.lat);
    return [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)];
  }, [apartments, officeCoords]);

  // Supercluster hook
  const { clusters, supercluster } = useSupercluster({
    points,
    bounds: bounds ?? [0, 0, 0, 0],
    zoom: viewState.zoom,
    options: { radius: 75, maxZoom: 17 },
  });

  // Handle cluster click - zoom in to expand
  const handleClusterClick = useCallback(
    (clusterId: number, longitude: number, latitude: number) => {
      if (!supercluster) return;
      const expansionZoom = Math.min(supercluster.getClusterExpansionZoom(clusterId), 17);
      setViewState({
        longitude,
        latitude,
        zoom: expansionZoom,
      });
    },
    [supercluster]
  );

  // Empty/loading states
  if (isLoading) {
    return (
      <Box
        style={{
          ...mapContainerStyle,
          backgroundColor: "#0f3460",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text c="dimmed">Loading map...</Text>
      </Box>
    );
  }

  if (apartments.length === 0) {
    return (
      <Box
        style={{
          ...mapContainerStyle,
          backgroundColor: "#0f3460",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Text c="dimmed">No apartments with location data</Text>
      </Box>
    );
  }

  return (
    <Box style={mapContainerStyle}>
      <Map
        {...viewState}
        onMove={(evt) => setViewState(evt.viewState)}
        style={{ width: "100%", height: "100%" }}
        mapStyle="https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json"
      >
        <NavigationControl position="top-right" />

        {(clusters as MapFeature[]).map((cluster) => {
          const [lng, lat] = cluster.geometry.coordinates;

          if (cluster.properties.cluster) {
            const clusterProps = cluster.properties as ClusterProperties;
            return (
              <Marker key={`cluster-${clusterProps.cluster_id}`} longitude={lng} latitude={lat}>
                <ClusterMarker
                  count={clusterProps.point_count}
                  onClick={() => handleClusterClick(clusterProps.cluster_id, lng, lat)}
                />
              </Marker>
            );
          }

          const apt = cluster.properties as ApartmentProperties;
          return (
            <Marker key={`apt-${apt.id}`} longitude={lng} latitude={lat}>
              <ApartmentMarkerWithHover apartment={apt} onClick={() => setSelectedApartment(apt)} />
            </Marker>
          );
        })}

        {selectedApartment && (
          <Popup
            longitude={selectedApartment.longitude}
            latitude={selectedApartment.latitude}
            onClose={() => setSelectedApartment(null)}
            closeButton={true}
            anchor="bottom"
            maxWidth="280px"
          >
            <ApartmentPopup apartment={selectedApartment} />
          </Popup>
        )}

        {officeCoords && (
          <Marker longitude={officeCoords.longitude} latitude={officeCoords.latitude}>
            <OfficeMarker />
          </Marker>
        )}
      </Map>
    </Box>
  );
}

// Cluster marker component
function ClusterMarker({ count, onClick }: { count: number; onClick: () => void }) {
  // Scale size based on count
  const size = Math.min(40 + Math.log2(count) * 5, 60);

  return (
    <Center
      onClick={onClick}
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        backgroundColor: "#e94560",
        color: "white",
        fontWeight: "bold",
        fontSize: size > 45 ? 16 : 14,
        border: "3px solid white",
        boxShadow: "0 2px 8px rgba(0,0,0,0.4)",
        cursor: "pointer",
        transition: "transform 0.1s ease",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.1)")}
      onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
    >
      {count}
    </Center>
  );
}

// Marker icon component (used as HoverCard target)
const ApartmentMarkerIcon = forwardRef<
  HTMLDivElement,
  { onClick: () => void; onMouseEnter?: () => void; onMouseLeave?: () => void }
>(function ApartmentMarkerIcon({ onClick, onMouseEnter, onMouseLeave, ...props }, ref) {
  return (
    <Center
      ref={ref}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "scale(1.15)";
        onMouseEnter?.();
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "scale(1)";
        onMouseLeave?.();
      }}
      {...props}
      style={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        backgroundColor: "#e94560",
        border: "3px solid white",
        boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
        cursor: "pointer",
        transition: "transform 0.1s ease",
      }}
    >
      <IconHome size={16} color="white" />
    </Center>
  );
});

// Individual apartment marker with hover popover (controlled Popover for MapLibre compatibility)
function ApartmentMarkerWithHover({
  apartment,
  onClick,
}: {
  apartment: MapApartment;
  onClick: () => void;
}) {
  const [opened, { open, close }] = useDisclosure(false);

  return (
    <Popover
      width={280}
      shadow="md"
      withArrow
      withinPortal
      zIndex={10000}
      opened={opened}
      position="top"
    >
      <Popover.Target>
        <ApartmentMarkerIcon onClick={onClick} onMouseEnter={open} onMouseLeave={close} />
      </Popover.Target>
      <Popover.Dropdown
        style={{ backgroundColor: "#16213e", border: "1px solid #0f3460", padding: 0 }}
        onMouseEnter={open}
        onMouseLeave={close}
      >
        {apartment.image && (
          <Image src={apartment.image} alt={apartment.title} h={120} fit="cover" />
        )}
        <Stack gap="xs" p="sm">
          <Text size="sm" fw={600} c="#eeeeee" lineClamp={1}>
            {apartment.title}
          </Text>
          <Group justify="space-between">
            <Text size="lg" fw={700} c="#e94560">
              {apartment.rent.toLocaleString()} kr
            </Text>
            <Group gap="xs">
              {apartment.rooms && (
                <Text size="xs" c="dimmed">
                  {apartment.rooms} rooms
                </Text>
              )}
              {apartment.size_sqm && (
                <Text size="xs" c="dimmed">
                  {apartment.size_sqm} m²
                </Text>
              )}
            </Group>
          </Group>
          <Group gap="xs">
            <Button
              component={Link}
              to="/apartments/$id"
              params={{ id: apartment.id.toString() }}
              size="xs"
              variant="filled"
              flex={1}
            >
              View Details
            </Button>
            <Button
              component="a"
              href={apartment.url}
              target="_blank"
              rel="noopener noreferrer"
              size="xs"
              variant="outline"
              flex={1}
              rightSection={<IconExternalLink size={14} />}
            >
              Qasa
            </Button>
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}

// Office marker component
function OfficeMarker() {
  return (
    <Center
      style={{
        width: 32,
        height: 32,
        borderRadius: "50%",
        backgroundColor: "#4caf50",
        border: "3px solid white",
        boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
      }}
    >
      <IconBuilding size={16} color="white" />
    </Center>
  );
}

// Popup content component
function ApartmentPopup({ apartment }: { apartment: MapApartment }) {
  return (
    <Paper
      p="sm"
      style={{
        minWidth: 220,
        backgroundColor: "#16213e",
        border: "1px solid #0f3460",
      }}
    >
      <Stack gap="xs">
        <Text size="sm" fw={600} c="#eeeeee" lineClamp={2}>
          {apartment.title}
        </Text>
        <Group justify="space-between" align="center">
          <Text size="lg" fw={700} c="#e94560">
            {apartment.rent.toLocaleString()} kr
          </Text>
          <StatusBadge status={apartment.status} />
        </Group>
        <Group gap="md">
          {apartment.rooms && (
            <Text size="xs" c="dimmed">
              {apartment.rooms} rooms
            </Text>
          )}
          {apartment.size_sqm && (
            <Text size="xs" c="dimmed">
              {apartment.size_sqm} m²
            </Text>
          )}
        </Group>
        <Anchor
          component={Link}
          to="/apartments/$id"
          params={{ id: apartment.id.toString() }}
          size="sm"
          c="#e94560"
        >
          View Details →
        </Anchor>
      </Stack>
    </Paper>
  );
}
